import {Injectable} from '@angular/core';
import {ContextMenuService} from "../context-menu/context-menu.service";
import {TableOperationExecutionService} from "../table-operation-execution/table-operation-execution.service";
import {ContextMenu} from "../context-menu/context-menu";
import {ViewDefinition} from "../../models/view/view-definition";
import {TableDefinition} from "../../models/table/table-definition";
import {ViewModelType} from "../../models/type/view-model-type.enum";
import {TableOperationModel} from "../../models/table-operation/table-operation-model";
import {TableOperationDefinition} from "../../models/table-operation/table-operation-definition";
import {ContentModel} from "../../models/content/content-model";
import {UtilService} from "../util/util.service";
import {ContextMenuItem} from "../context-menu/context-menu-item";
import {RequestService} from "../request/request.service";
import {MagicRendererProperties} from "../../models/properties/magic-renderer-properties.enum";
import {LegacyNavigateService} from "../legacy-navigate/legacy-navigate.service";
import {TableOperationButton} from "./table-operation-button";
import {UserService} from "../user/user.service";
import {ListContentModel} from "../../models/content/list-content-model";
import {InternationalizationService} from "../internationalization/internationalization.service";
import {DetailContentModel} from "../../models/content/detail-content-model";

interface WorkflowActivityItem {
  i18nKey: string;
  activityData: object;
}

@Injectable({
  providedIn: 'root'
})
export class TableOperationService {

  constructor(private contextMenuService: ContextMenuService,
              private userService: UserService,
              private i18nService: InternationalizationService,
              private navigateService: LegacyNavigateService,
              private requestService: RequestService,
              private tableOperationExecutionService: TableOperationExecutionService) { }

  public configureViewConfigMenu(contentModel: ContentModel): ContextMenu {

    const filterWith = (operation: TableOperationDefinition, viewModelType: ViewModelType) => {
      return viewModelType === ViewModelType.LIST ? operation._showInListConfigMenu : operation._showInDetailConfigMenu;
    }

    const operations = this.getTableOperations(contentModel, contentModel.viewModel.viewDefinition(), contentModel.tableModel.tableDefinition(), true, filterWith);

    if (operations.length === 0) {
      return null; // TODO: Indicate to hide the element that this menu is on?
    }

    const menuItems = [];

    const tableName = contentModel.viewModel.viewDefinition().table;

    const contextMenu = this.contextMenuService.createContextMenu();

    operations.forEach((operation, idx) => {

      if (operation.separatorBefore()) {
        menuItems.push(contextMenu.createContextMenuSeparator());
      }

      const label = this.i18nService.getMessage(operation.getI18nKey());

      const actionCallback = (event: any) => {
        return this.tableOperationExecutionService.execute(
          tableName,
          operation,
          this.convertContentModel(contentModel),
          {},
          contentModel.data,
          ViewModelType.CONFIG);
      };

      menuItems.push(contextMenu.createContextMenuItem(label, actionCallback, null, operation.getIcon()));

      if (operation.separatorAfter()) {
        menuItems.push(contextMenu.createContextMenuSeparator());
      }
    });

    contextMenu.registerCreateCallback((contextMenu) => Promise.resolve(menuItems));

    return contextMenu;
  }


  /**
   * Configures the context menu for a list view.
   *
   * @param {ContentModel} contentModel - The content model.
   * @param {any[]} selectedRecords - The selected records.
   * @param {string} selectedFieldName - The selected field name.
   * @param {boolean} isRelatedList - Flag indicating if the list is a related list.
   * @param {any} relatedListParentRecord - The parent record of the related list.
   * @param {string} relatedListParentRecordId - The ID of the parent record of the related list.
   * @param {string} relatedListParentRecordTable - The table of the parent record of the related list.
   * @param {string} relatedListParentRelation - The relation of the parent record of the related list.
   * @returns {ContextMenu} The configured context menu.
   */
  public configureListContextMenu(contentModel: ContentModel,
                                  selectedRecords: any[],
                                  selectedFieldName: string,
                                  isRelatedList: boolean,
                                  relatedListParentRecord: any,
                                  relatedListParentRecordId: string,
                                  relatedListParentRecordTable: string,
                                  relatedListParentRelation: string): ContextMenu {

    const showBulkOperationsOnly = selectedRecords.length > 1;
    const selectedRecordIds = selectedRecords.map((rec) => rec.id);
    const tableName = contentModel.tableModel.getTableName();

    const contextMenu = this.contextMenuService.createContextMenu();

    const executeTableOperation = (operation, selectedRecords) => {
      return (event) => {

        const options = Object();

        options.fieldName = selectedFieldName;
        options.parent = relatedListParentRecord;

        // If the related list is a Relation to a Parent Record, we need to pass the following information
        if (relatedListParentRelation != null) {
          options.parentId = relatedListParentRecordId;
          options.parentTable = relatedListParentRecordTable;
          options.parentRelation = relatedListParentRelation;
        }

        // Create an executor and optionally refresh the rootViewModel on success (to show the update on the list)
        this.tableOperationExecutionService.executeMultiple(
          tableName,
          operation,
          this.convertContentModel(contentModel),
          options,
          selectedRecords,
          ViewModelType.LIST
        );
      };
    };

    const evaluateClientScriptCondition = (operation, selectedRecord) => {
      return this.tableOperationExecutionService.processCondition(
        tableName,
        operation,
        this.convertContentModel(contentModel),
        {
          fieldName: selectedFieldName,
          parent: relatedListParentRecord,
        },
        selectedRecord,
        ViewModelType.LIST
      );
    };

    const addTableOperations = (contextMenu: ContextMenu,
                                applicableOperations: { tableOperationId: string, applicableRecordIds: string[] }[]) => {

      // At this point, the applicableRecordIds property holds an array of record IDs if the operation has passed
      // server side checks: permissions, and server side condition script (if applicable). Now we evaluate operations
      // that have a client side condition script.

      const menuItemsAndOperations = applicableOperations.map((applicableOperation) => {

        const operation = contentModel.tableModel.getTableOperationByID(applicableOperation.tableOperationId);

        if (operation) {
          if (operation.executeConditionOnClient()) {
            // For each selected record
            selectedRecords.forEach((selectedRecord, index) => {
              if (!evaluateClientScriptCondition(operation, selectedRecord)) {
                // Then remove from the list of applicable records
                applicableOperation.applicableRecordIds.splice(applicableOperation.applicableRecordIds.indexOf(selectedRecord.id), 1);
              }
            });
          }

          if (applicableOperation.applicableRecordIds.length > 0) {

            const appliesToRecords = selectedRecords.filter((r) => applicableOperation.applicableRecordIds.indexOf(r.id) !== -1);

            const labelSuffix = (appliesToRecords.length < selectedRecords.length) ?
              this.i18nService.getMessage('contextmenu.label.itemcount', {
                0: appliesToRecords.length,
                1: selectedRecords.length
              }) :
              undefined;

            const contextMenuItem = contextMenu.createContextMenuItem(
              this.i18nService.getMessage(operation.getI18nKey()) + (operation.isCustomOperation() ? (UtilService.isGUID(operation.getID()) ? "" : " (Java)") : " (builtin)"),
              executeTableOperation(operation, appliesToRecords),
              labelSuffix,
              operation.getIcon()
            );

            return {
              operation: operation,
              item: contextMenuItem,
            };
          }
        }

        return undefined;
      }).filter((m) => !!m);

      menuItemsAndOperations.sort((lhs, rhs) => lhs.operation.compareOrder(rhs.operation));

      return menuItemsAndOperations.map((itemAndSeparator, index) => {
        const items = [];
        itemAndSeparator.operation.separatorBefore() && items.push(contextMenu.createContextMenuSeparator());
        items.push(itemAndSeparator.item);
        itemAndSeparator.operation.separatorAfter() &&  items.push(contextMenu.createContextMenuSeparator());
        return items;
      }).flat();
    };

    const buildListContextMenuOperations = (contextMenu) => {
      const method = isRelatedList ? 'getRelatedListContextMenuOperations' : 'getListContextMenuOperations';
      return new Promise<ContextMenuItem[]>((resolve, reject) => {
        this.requestService
          .createRequest()
          .controller("servicely.core.System")
          .method(method)
          .table(tableName)
          .data({
            recordIds: selectedRecordIds,
            relatedListParentRecordId: relatedListParentRecordId,
            relatedListParentRecordTable: relatedListParentRecordTable,
          })
          .successFunc(function (response) {
            resolve(addTableOperations(contextMenu, response?.data?.result || []));
          })
          .errorFunc(function(response) {
            reject(response.responseText);
          })
          .execute();
      });
    };

    contextMenu.registerCreateCallback(buildListContextMenuOperations);

    if (showBulkOperationsOnly === false) {
      if (selectedRecords && selectedRecords.length > 0) {
        const workflowData = selectedRecords[0]._workflow;

        const items = this.getWorkflowActivityItems(workflowData);

        if (items.length > 0) {
          contextMenu.addContextMenuSeparator();
          const menuItems = items.map((workflowActivityItem) => {
            const operation = TableOperationModel.createWorkflowActivityTableOperationModel(workflowActivityItem);
            return contextMenu.createContextMenuItem(this.i18nService.getMessage(workflowActivityItem.i18nKey), executeTableOperation(operation, selectedRecords));
          });
          contextMenu.registerCreateCallback((contextMenu) => Promise.resolve(menuItems));
        }
      }
    }

    return contextMenu;
  }

  public configureFieldContextMenu(contentModel: ContentModel, fieldDefinition: any): ContextMenu {

    const contextMenu = this.contextMenuService.createContextMenu();

    const menuItems = [];

    const tableName = fieldDefinition.table;

    const filterWith = (operation: TableOperationDefinition) => operation._showInDetailContextMenu;

    const operations = this.getTableOperations(contentModel, contentModel.viewModel.viewDefinition(), contentModel.tableModel.tableDefinition(), true, filterWith);

    operations.forEach((operation) => {
      if (operation.separatorBefore()) {
        menuItems.push(contextMenu.createContextMenuSeparator());
      }

      const label = this.i18nService.getMessage(operation.getI18nKey());

      const actionCallback = (event: any) => {
        this.tableOperationExecutionService.execute(
          tableName,
          operation,
          this.convertContentModel(contentModel),
          {},
          contentModel.data,
          ViewModelType.CONFIG);
      };

      menuItems.push(contextMenu.createContextMenuItem(label, actionCallback, null, operation.getIcon()));

      if (operation.separatorAfter()) {
        menuItems.push(contextMenu.createContextMenuSeparator());
      }

    });

    contextMenu.registerCreateCallback((contextMenu) => Promise.resolve(menuItems));

    return contextMenu;
  }

  public tableOperationButtons(contentModel: ContentModel): TableOperationButton[] {

    const viewModel = contentModel.viewModel;
    const detailViewModel = (contentModel as DetailContentModel)?.getDetailViewModel?.();
    const tableName = contentModel.viewModel.viewDefinition().table;

    const tableOperationFilter = detailViewModel?.getTableOperationFilter?.() || viewModel.getTableOperationFilter();
    const defaultFilter = (operationDefinition: TableOperationDefinition, viewModelType: ViewModelType) => {
      return viewModelType === ViewModelType.LIST ? operationDefinition._showInListView : operationDefinition._showInDetailView;
    };

    const filterWith = tableOperationFilter || defaultFilter;

    const operations = this.getTableOperations(contentModel, contentModel.viewModel.viewDefinition(), contentModel.tableModel.tableDefinition(), true, filterWith);

    // TODO: the support in the KO code for the RequiredFields popup has not been implemented here as it is very KO specific.
    // It should be looked at when the detail view is reimplemented.

    const mappedOperations =  operations.map((operation) => {

      const actionCallback = (event: any) => {
        const execute = (data) => {
          this.tableOperationExecutionService.execute(
            tableName,
            operation,
            this.convertContentModel(contentModel),
            {
              event: event
            },
            data,
            contentModel.viewModel.getViewModelType());
        };

        const executeMultiple = (data) => {
          this.tableOperationExecutionService.executeMultiple(
            tableName,
            operation,
            this.convertContentModel(contentModel),
            {
              event: event
            },
            data,
            contentModel.viewModel.getViewModelType());
        };

        if (contentModel.viewModel.getViewModelType() === ViewModelType.LIST) {
          const listGrid = (contentModel as ListContentModel).getListGrid();
          const selectedRecords = listGrid.getSelectedRecords();
          const selectedContentModelData = selectedRecords.map((record) => record._dataFn().record);

          if (selectedContentModelData.length === 0) {
            // Button in list view with no selected records
            if (operation.isServerOperation()) {
              // We cannot execute a server operation without a record
              console.warn("TableOperationService: not executing server operation as no record available");
            } else {
              // We can execute a client operation without a record
              execute(null);
            }
          } else {
            executeMultiple(selectedContentModelData);
          }
        } else {
          // Button in detail view
          execute(contentModel.data);
        }
      };

      const rendererProperties = operation.getRendererProperties();
      const customIconCss = rendererProperties[MagicRendererProperties.BUTTON_ICON_CSS];
      const prefixIcon = operation.getIcon();

      return {
        label: this.i18nService.getMessage(operation.getI18nKey()) + (operation.isCustomOperation() ? (UtilService.isGUID(operation.getID()) ? "" : " (Java)") : " (builtin)"),
        actionCallback: actionCallback,
        contextMenu: this.createButtonContextMenu(operation.getID()),
        cssClass: rendererProperties[MagicRendererProperties.BUTTON_TYPE],
        prefixIcon: prefixIcon,
        customIconCss: customIconCss,
        options: {
          id: "ent_op_" + operation.getID() // Give the button an ID we can use from automation software
        }
      };
    });

    return mappedOperations;
  }

  public workflowButtons(contentModel: ContentModel): TableOperationButton[] {

    const viewModel = contentModel.viewModel;
    const detailViewModel = (contentModel as DetailContentModel)?.getDetailViewModel?.();
    const tableName = contentModel.viewModel.viewDefinition().table;
    const data = contentModel.data;
    const tableDefinition = contentModel.tableModel.tableDefinition();

    const tableOperationFilter = detailViewModel?.getTableOperationFilter?.() || viewModel.getTableOperationFilter() || (x => true);

    const workflowData = data._workflow;

    const items = this.getWorkflowActivityItems(workflowData).filter(tableOperationFilter);

    return items.map((workflowActivityItem) => {
      const operation = TableOperationModel.createWorkflowActivityTableOperationModel(workflowActivityItem);
      let createButton = true;

      if (operation.hasCondition()) {
        createButton = this.passesCondition(tableDefinition, null, operation, contentModel, ViewModelType.DETAIL);
      }

      // TODO: the support in the KO code for the RequiredFields popup has not been implemented here as it is very KO specific.
      // It should be looked at when the detail view is reimplemented.

      if (createButton) {
        const actionCallback = (event: any) => {
          return this.tableOperationExecutionService.execute(
            tableName,
            operation,
            this.convertContentModel(contentModel),
            {
              event: event
            },
            contentModel.data,
            ViewModelType.DETAIL);
        };

        return {
          label: this.i18nService.getMessage(workflowActivityItem.i18nKey),
          actionCallback: actionCallback,
          cssClass: "info",
          prefixIcon: null,
          options: {
            id: "workflow_button_" + operation.getID(), // Give the button an ID we can use from automation software,
            operationId: operation.getID(),
            code: operation.getCode(),
          }
        };
      } else {
        return null;
      }
    }).filter((item) => item !== null && item !== undefined);
  }

  public formTemplates(contentModel: ContentModel) {
    // TODO: not yet implemented
    return null;
  }

  private createButtonContextMenu(operationID: string): ContextMenu {
    return this.contextMenuService.createContextMenu().addContextMenuItems(
      [

        this.contextMenuService.createContextMenuItem(
          this.i18nService.getMessage("operation.edit.popup"),
          (event) => {
            this.navigateService.rightSlideInWindow({
                'tile': 'DetailsAutoScaffold',
                'parameter-values': {
                  table: "TableOperation",
                  id: operationID
                }
              },
              function (operation) {
              });
          },
          undefined,
          "edit"),

        this.contextMenuService.createContextMenuItem(
          this.i18nService.getMessage("operation.edit.full"),
          (event) => {
            this.navigateService.to({
                table: "TableOperation",
                id: operationID
              });
          },
          undefined,
          "edit"
        )
      ]
    );
  }

  private getTableOperations(contentModel: ContentModel, viewDefinition: ViewDefinition, tableDefinition: TableDefinition, sorted: boolean, filter: (operation: TableOperationDefinition, type: ViewModelType) => boolean): TableOperationModel[] {

    const filteredOperations = viewDefinition.viewModelType === ViewModelType.LIST ?
      this.listViewFilter(tableDefinition, viewDefinition, contentModel, filter) :
      this.detailViewFilter(tableDefinition, viewDefinition, contentModel, filter);

    return sorted ? filteredOperations.sort((lhs, rhs) => lhs.compareOrder(rhs)) : filteredOperations;
  }

  private listViewFilter(tableDefinition: TableDefinition, viewDefinition: ViewDefinition, contentModel: ContentModel, filter: (operation: TableOperationDefinition, type: ViewModelType) => boolean): TableOperationModel[] {
    const operationIds = viewDefinition._tableOperations;
    const operations = tableDefinition._tableOperations;
    return operationIds.map((id) => {
        let result;
        const operation = operations.find((op) => op._id === id);
        if (operation) {
          const matchesFilter = typeof filter === "function" ? filter(operation, ViewModelType.LIST) : true;
          const tableOperationModel = new TableOperationModel(operation);
          if (matchesFilter && this.passesCondition(tableDefinition, null, tableOperationModel, contentModel, ViewModelType.LIST)) {
            result = tableOperationModel;
          }
        } else {
          console.error("TableOperationService.listViewFilter - cannot find operation for ", id);
        }
        return result;
      }
    ).filter((op) => op !== null && op !== undefined);
  }

  private detailViewFilter(tableDefinition: TableDefinition, viewDefinition: ViewDefinition, contentModel: ContentModel, filter: (operation: TableOperationDefinition, type: ViewModelType) => boolean): TableOperationModel[] {
    const operations = tableDefinition._tableOperations;
    const operationIds = UtilService.unwrapObservable(contentModel.data._tableOperations);

    return operationIds.map((id) => {
        let result;
        const operation = operations.find((op) => op._id === id);
        if (operation) {
          const matchesFilter = typeof filter === "function" ? filter(operation, ViewModelType.DETAIL) : true;
          const tableOperationModel = new TableOperationModel(operation);
          if (matchesFilter && this.passesCondition(tableDefinition, null, tableOperationModel, contentModel, ViewModelType.DETAIL)) {
            result = tableOperationModel;
          }
        } else {
          // PF: I don't know why these appear in the table operation list
          if (id != "delete" && id != "update") {
            console.debug("TableOperationService.detailViewFilter - cannot find operation for ", id);
          }
        }
        return result;
      }
    ).filter((op) => op !== null && op !== undefined);
  }

  private passesCondition(tableDefinition: TableDefinition, field: any /*FieldDefinition or what?*/, operation: TableOperationModel, contentModel: ContentModel, formType: ViewModelType): boolean {
    const options: any = {};

    if (field) {
      options.fieldName = field.fieldName;
    }

    return this.tableOperationExecutionService.processCondition(
      tableDefinition._tableClass,
      operation,
      this.convertContentModel(contentModel),
      options,
      contentModel.data,
      formType);
  }

  // Convert an angular content model to an NG ViewModel (ListViewModel or DetailViewModel).
  // If the content model has been converted already we don't do it again. This may not be necessary.
  private convertContentModel(contentModel: ContentModel): any {
    if (!contentModel._convertedViewModel) {
        UtilService.notImplemented("TableOperationService", "convertContentModel");
        contentModel._convertedViewModel = contentModel;
    }
    return contentModel._convertedViewModel;
  }

  private getWorkflowActivityItems(workflowData: any): WorkflowActivityItem[] {

    if (workflowData == null) {
      return [];
    }

    // Workflow data wll be a KO observable if coming from a detail form. It will be unmapped if a list.
    workflowData = (window as any).KO_MAPPING.toJS(workflowData);

    const result: WorkflowActivityItem[] = [];

    const activityType = workflowData.activityType;
    const transitions = workflowData.transitions;

    switch (activityType) {
      case "WorkflowUserTransitionActivity":

        // Create one button per _workflow transition
        transitions.forEach((transition) => {
          const activityData = {
            workflowActionType: "transition",
            forApproval: false,
            transitionID: transition.id,
            code: transition.code,
            executeOnClient: transition.executeOnClient === true,
            condition: transition.condition ? transition.condition: null,
            onBefore: transition.onBefore ? transition.onBefore: null,
            onSuccess: transition.onSuccess ? transition.onSuccess: null
          };

          const workflowActivityItem: WorkflowActivityItem = {
            i18nKey: transition.i18nKey,
            activityData: activityData
          } ;

          result.push(workflowActivityItem);
        });

        break;
      case "Approval":
      case "WorkflowApprovalActivity":

        // Note: see ApprovalActivity class serialize method and comments for SR-1095 for when
        // workflowTask.type() is an empty string
        if (workflowData.workflowTask && workflowData.workflowTask.type !== "") {
          const workflowTask = workflowData.workflowTask;

          result.push({i18nKey: "activity.approve", activityData: {
            workflowActionType: "activity",
            forApproval: true,
            taskID: workflowTask.workflowTaskID,
            action: "approved",
            onBefore: `return WorkflowActivitySelector.approvalButtonCallback(tableName, id, workflowActivityData);`,
            onSuccess: `DialogAPI.success(I18n.getMessage("actionresult.actionsuccess.name"));`
          }});

          result.push({i18nKey:"activity.reject", activityData: {
            workflowActionType: "activity",
            forApproval: true,
            taskID: workflowTask.workflowTaskID,
            action: "rejected",
            onBefore: `return WorkflowActivitySelector.rejectButtonCallback(tableName, id, workflowActivityData);`,
            onSuccess: `DialogAPI.success(I18n.getMessage("actionresult.actionsuccess.name"));`
          }});

          // Re-activate once 'more-info' is available
          // workflowActivityItems.push({i18nKey:"activity.more-info", activityData: {
          //     workflowActionType: "activity",
          //     forApproval: true,
          //     taskID: workflowTask.workflowTaskID(),
          //     action: "more-info",
          //     onBefore: `return WorkflowActivitySelector.moreInfoCallback(tableName, id, workflowActivityData);`
          // }});;
        }
        break;
    }

    return result;
  }

}
